/* eslint-disable import/no-anonymous-default-export */
import React from 'react';
import { db } from '../../../services/firebase';
import {
  collection,
  onSnapshot,
  addDoc,
  where,
  query,
  orderBy,
  getDocs,
  updateDoc,
  doc,
  limit,
  startAfter,
} from 'firebase/firestore';

interface IState {
  isLoading: boolean;
  isLoadingJoinKonsultasi: boolean;
  isLoadingFetchLogChat: boolean;
  error: boolean;
  conversationPage: boolean;
  mainPage: boolean;
  logChat: any[];
  chat: any[];
  idChatCurrent: string;
  contentMessage: string;
  selectedMessage: any[];
  showChat: boolean;
  historyPage: boolean;
  hasMoreLogChat: boolean;
  lastLogChatDoc: any;
}

interface InitialState {
  state: IState;
  backPage: Function;
  toConversationPage: Function;
  toHistoryPage: Function;
  getLogChat: Function;
  getDetailChat: Function;
  loadMoreLogChat: Function;
  startKonsultasi: Function;
  sendMessage: Function;
  setContentMessage: Function;
  setSelectedMessage: Function;
  setShowChat: Function;
  unsubscribeLogChat: Function;
  unsubscribeDetailChat: Function;
}

const initialState = {
  state: {
    isLoading: false,
    isLoadingJoinKonsultasi: false,
    isLoadingFetchLogChat: false,
    error: false,
    conversationPage: false,
    mainPage: true,
    logChat: [],
    chat: [],
    idChatCurrent: '',
    contentMessage: '',
    selectedMessage: [],
    showChat: false,
    historyPage: false,
    hasMoreLogChat: true,
    lastLogChatDoc: null,
  },
  backPage: () => {},
  toConversationPage: () => {},
  getLogChat: () => {},
  toHistoryPage: () => {},
  getDetailChat: () => {},
  loadMoreLogChat: () => {},
  startKonsultasi: () => {},
  sendMessage: () => {},
  setContentMessage: () => {},
  setSelectedMessage: () => {},
  setShowChat: () => {},
  unsubscribeLogChat: () => {},
  unsubscribeDetailChat: () => {},
};

const Context = React.createContext<InitialState>(initialState);
const { Provider: ChatProvider } = Context;

const Provider: React.FC = ({ children }) => {
  const [state, setState] = React.useState<IState>({
    isLoading: false,
    isLoadingJoinKonsultasi: false,
    isLoadingFetchLogChat: false,
    error: false,
    conversationPage: false,
    mainPage: true,
    logChat: [],
    chat: [],
    idChatCurrent: '',
    contentMessage: '',
    selectedMessage: [],
    showChat: false,
    historyPage: false,
    hasMoreLogChat: true,
    lastLogChatDoc: null,
  });

  const logChatRef = collection(db, 'log_chat');
  const chatRef = collection(db, 'chat');
  const unsubscribeRef = React.useRef<{ logChat: (() => void) | null; detailChat: (() => void) | null }>({
    logChat: null,
    detailChat: null,
  });

  // Cleanup listeners on unmount
  React.useEffect(() => {
    const currentUnsubscribe = unsubscribeRef.current;
    return () => {
      if (currentUnsubscribe.logChat) {
        currentUnsubscribe.logChat();
      }
      if (currentUnsubscribe.detailChat) {
        currentUnsubscribe.detailChat();
      }
    };
  }, []);

  const setLoading = (value: boolean) => {
    setState((prevstate) => ({
      ...prevstate,
      isLoading: value,
    }));
  };

  const setShowChat = (value: boolean) => {
    setState((prevstate) => ({
      ...prevstate,
      showChat: value,
    }));
  };

  const setLoadingJoinKonsultasi = (value: boolean) => {
    setState((prevstate) => ({
      ...prevstate,
      isLoadingJoinKonsultasi: value,
    }));
  };

  const setLoadingFetchLogChat = (value: boolean) => {
    setState((prevstate) => ({
      ...prevstate,
      isLoadingFetchLogChat: value,
    }));
  };

  const backPage = () => {
    setState((prevstate) => ({
      ...prevstate,
      conversationPage: false,
      mainPage: true,
      historyPage: false,
    }));
  };

  const toConversationPage = () => {
    setState((prevstate) => ({
      ...prevstate,
      conversationPage: true,
      mainPage: false,
      historyPage: false,
    }));
  };

  const setContentMessage = (message: string) => {
    setState((prevstate) => ({
      ...prevstate,
      contentMessage: message,
    }));
  };

  const setSelectedMessage = (idx: number) => {
    setState((prevstate) => ({
      ...prevstate,
      selectedMessage: [state.logChat[idx]],
      idChatCurrent: state.logChat[idx].id,
    }));
  };

  const getLogChat = async (reset = false) => {
    if (reset) {
      setLoadingFetchLogChat(true);
      setState((prevstate) => ({
        ...prevstate,
        logChat: [],
        lastLogChatDoc: null,
        hasMoreLogChat: true,
      }));
    }

    // Cleanup previous listener
    if (unsubscribeRef.current.logChat) {
      unsubscribeRef.current.logChat();
    }

    let logRefChat;
    if (state.lastLogChatDoc && !reset) {
      // Load more with pagination
      logRefChat = query(
        collection(db, 'log_chat'),
        orderBy('timestamp', 'desc'),
        startAfter(state.lastLogChatDoc),
        limit(50)
      );
    } else {
      // Initial load
      logRefChat = query(
        collection(db, 'log_chat'),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
    }

    try {
      const unsubscribe = onSnapshot(logRefChat, (snapshot) => {
        const newLogChat = snapshot.docs.map((doc) => ({
          id: doc.id,
          data: doc.data(),
        }));
        
        setState((prevstate) => {
          const updatedLogChat = reset 
            ? newLogChat 
            : [...prevstate.logChat, ...newLogChat];
          
          return {
            ...prevstate,
            logChat: updatedLogChat,
            lastLogChatDoc: snapshot.docs[snapshot.docs.length - 1],
            hasMoreLogChat: snapshot.docs.length === 50,
          };
        });

        setLoadingFetchLogChat(false);
      }, (error) => {
        console.error('Error fetching log chat:', error);
        setLoadingFetchLogChat(false);
      });
      
      unsubscribeRef.current.logChat = unsubscribe;
    } catch (error) {
      console.error('Error setting up log chat listener:', error);
      setLoadingFetchLogChat(false);
    }
  };

  const loadMoreLogChat = async () => {
    if (!state.hasMoreLogChat || state.isLoadingFetchLogChat) return;

    if (unsubscribeRef.current.logChat) {
      unsubscribeRef.current.logChat();
    }

    const logRefChat = query(
      collection(db, 'log_chat'),
      orderBy('timestamp', 'desc'),
      startAfter(state.lastLogChatDoc),
      limit(50)
    );

    try {
      const unsubscribe = onSnapshot(logRefChat, (snapshot) => {
        const newLogChat = snapshot.docs.map((doc) => ({
          id: doc.id,
          data: doc.data(),
        }));
        
        setState((prevstate) => ({
          ...prevstate,
          logChat: [...prevstate.logChat, ...newLogChat],
          lastLogChatDoc: snapshot.docs[snapshot.docs.length - 1],
          hasMoreLogChat: snapshot.docs.length === 50,
        }));

        setLoadingFetchLogChat(false);
      }, (error) => {
        console.error('Error fetching more log chat:', error);
        setLoadingFetchLogChat(false);
      });

      unsubscribeRef.current.logChat = unsubscribe;
    } catch (error) {
      console.error('Error setting up more log chat listener:', error);
      setLoadingFetchLogChat(false);
    }
  };

  const getDetailChat = () => {
    setLoading(true);

    var chatRefByValue;

    if (state.idChatCurrent) {
      chatRefByValue = query(
        collection(db, 'chat'),
        orderBy('timestamp', 'asc'),
        where('id_log_chat', '==', state.idChatCurrent)
      );
    }

    try {
      onSnapshot(chatRefByValue, (snapshot) => {
        setState((prevstate) => ({
          ...prevstate,
          chat: [],
        }));

        snapshot.docs.map((doc) => {
          return setState((prevstate) => ({
            ...prevstate,
            chat: [...prevstate.chat, { id: doc.id, data: doc.data() }],
          }));
        });

        setLoading(false);
      });
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const startKonsultasi = (
    id_materi: number,
    id_user: number,
    is_done: boolean,
    judul_materi: string,
    nama_user: string
  ) => {
    const timestamp = new Date();
    setLoadingJoinKonsultasi(true);

    var chatRefByValue;
    var is_read = true;

    if (id_user) {
      chatRefByValue = query(
        collection(db, 'log_chat'),
        where('id_user', '==', id_user)
      );
    }

    getDocs(chatRefByValue)
      .then((response) => {
        // check if response is empty, create data. but is not just update data
        if (response.empty) {
          // add doc
          addDoc(logChatRef, {
            id_materi,
            id_user,
            is_done,
            judul_materi,
            nama_user,
            timestamp,
          })
            .then((response) => {
              setState((prevstate) => ({
                ...prevstate,
                idChatCurrent: response.id,
              }));

              toConversationPage();
              setLoadingJoinKonsultasi(false);
            })
            .catch((err) => console.log(err));
        } else {
          var docChatRef;
          var chatRef;
          response.docs.map((value) => {
            docChatRef = doc(db, 'log_chat', value.id);
            chatRef = query(
              collection(db, 'chat'),
              where('id_log_chat', '==', value.id),
              where('is_admin', '==', true),
              where('is_read', '==', false)
            );
            return setState((prevstate) => ({
              ...prevstate,
              idChatCurrent: value.id,
            }));
          });

          getDocs(chatRef)
            .then((resp) => {
              resp.docs.map((value) => {
                // update chat
                return updateDoc(doc(db, 'chat', value.id), { is_read })
                  .then(() => {})
                  .catch((err) => console.log(err));
              });

              // update log chat
              return updateDoc(docChatRef, { timestamp })
                .then(() => {
                  toConversationPage();
                  setLoadingJoinKonsultasi(false);
                })
                .catch((err) => console.log(err));
            })
            .catch((err) => console.log(err));
        }
      })
      .catch((err) => console.log(err));
  };

  /**
   *
   * @param id_user
   * @param is_admin
   * @param nama_user
   */
  const sendMessage = (
    id_user: number,
    is_admin: boolean,
    nama_user: string
  ) => {
    var id_log_chat = state.idChatCurrent;
    var content = state.contentMessage;
    const timestamp = new Date();
    var is_read = false;

    // add doc
    addDoc(chatRef, {
      id_log_chat,
      id_user,
      is_admin,
      is_read,
      content,
      nama_user,
      timestamp,
    })
      .then((response) => {})
      .catch((err) => console.log(err));
  };

  /**
   *
   * @param id_user
   */
  const toHistoryPage = (id_user: number) => {
    setState((prevstate) => ({
      ...prevstate,
      historyPage: true,
      mainPage: false,
    }));

    getLogChatByIdUser(id_user);
  };

  /**
   *
   * @param id_user
   */
  const getLogChatByIdUser = (id_user: number) => {
    setLoadingFetchLogChat(true);
    var chatRefByValue;

    if (id_user) {
      chatRefByValue = query(
        collection(db, 'log_chat'),
        where('id_user', '==', id_user)
      );
    }
    getDocs(chatRefByValue)
      .then((response) => {
        setState((prevstate) => ({
          ...prevstate,
          logChat: [],
        }));
        response.docs.map((doc) => {
          return setState((prevstate) => ({
            ...prevstate,
            logChat: [...prevstate.logChat, { id: doc.id, data: doc.data() }],
          }));
        });
        setLoadingFetchLogChat(false);
      })
      .catch((err) => {
        console.log(err);
        setLoadingFetchLogChat(true);
      });
  };

  const unsubscribeLogChat = () => {
    if (unsubscribeRef.current.logChat) {
      unsubscribeRef.current.logChat();
      unsubscribeRef.current.logChat = null;
    }
  };

  const unsubscribeDetailChat = () => {
    if (unsubscribeRef.current.detailChat) {
      unsubscribeRef.current.detailChat();
      unsubscribeRef.current.detailChat = null;
    }
  };

  return (
    <ChatProvider
      value={{
        state,
        backPage,
        toConversationPage,
        getLogChat,
        getDetailChat,
        loadMoreLogChat,
        startKonsultasi,
        sendMessage,
        setContentMessage,
        setSelectedMessage,
        setShowChat,
        toHistoryPage,
        unsubscribeLogChat,
        unsubscribeDetailChat,
      }}
    >
      {children}
    </ChatProvider>
  );
};

export const useChatContext = () => React.useContext(Context);

export default {
  useChatContext,
  Provider,
};
